<script setup lang="ts">
import { useRefHistory } from "@vueuse/core";
import { ElMessage, ElMessageBox } from "element-plus";
import { cloneDeep, isEqual, pick } from "lodash-es";
import { computed, onMounted, ref, toRaw, watch } from "vue";
import { EntityDefinitionListDto, SysOrg, SysOrgModule } from "~/api";
import { useEntityDefinitionStore } from "~/stores/EntityDefinitionStore";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useOrgStore } from "~/stores/OrgStore";
import { apiClient } from "~/utils/apiClient";
import { drawerWidth } from "~/utils/constants";
import { clearErrors, showError } from "~/utils/handleAPIError";
import { arraysAreIdentical } from "~/utils/miscUtils";
import { getEnvConfig } from "~/services/appConfig";

import { authStore } from "~/stores/authStore";
import OrgJsonImporter from "./OrgJsonImporter.vue";
const authToken = ref<string>();

const apiBase = getEnvConfig().serverUrl;

const defaultOrg = {
  code: "",
  name: "",
  guid: undefined,
  entityDefinitionGuid: undefined,
  customCSS: "",
  modules: [],
};

/** Extract only the SysOrg properties from an object, filtering out OrgPlus extensions */
const extractOrgProperties = (obj: any): SysOrg => {
  // Pick only the properties that are user-editable in this component
  // Exclude loginRoleOrgs and orgGroups as they are managed elsewhere
  return pick(obj, [
    "guid",
    "code",
    "name",
    "contactNumber",
    "email",
    "emailDomains",
    "websiteUrl",
    "customCSS",
    "entityDefinitionGuid",
    "logoFileGuid",
    "modules",
  ]) as SysOrg;
};

const props = defineProps<{
  org?: SysOrg;
}>();

const emit = defineEmits<{
  (e: "update:org", value: SysOrg): void;
  (e: "add:org", value: SysOrg): void;
}>();

const orgStore = useOrgStore();
const entityDefinitionStore = useEntityDefinitionStore();
const myInfoStore = useMyInfoStore();

const activeTabName = ref("edit");
const uploadingLogo = ref(false);
const exportingOrg = ref(false);
const importDialogVisible = ref(false);
const logoUploadRef = ref();

const getAllEmailDomains = () => {
  const validDomains: string[] = [];
  orgStore.List.forEach((org) => {
    const tempEmailDomains = org.emailDomains
      ?.split(",")
      .map((domain) => domain.trim());
    tempEmailDomains?.forEach((e) => {
      if (!validDomains.includes(e) && e !== "") {
        validDomains.push(e);
      }
    });
  });

  return validDomains;
};

const logoImageUrl = ref<string | null>(null);

const loadLogo = async (fileGuid: string | undefined | null) => {
  if (!authToken.value || !fileGuid) {
    logoImageUrl.value = null;
    return;
  }

  try {
    const response = await fetch(`${apiBase}/api/files/${fileGuid}`, {
      headers: {
        Authorization: `Bearer ${authToken.value}`,
      },
    });

    if (!response.ok) {
      logoImageUrl.value = null;
      return;
    }

    const blob = await response.blob();
    logoImageUrl.value = URL.createObjectURL(blob);
  } catch (err) {
    showError(`Error generating thumbnail for fileGuid "${fileGuid}":`, err);
    logoImageUrl.value = null;
  }
};

/** local copy of the org we are editing/creating */
const localOrg = ref<SysOrg>(
  // use copy of provided org if defined, otherwise use default one
  // @ts-expect-error - we want the defaultOrg guid to be undefined so it can be populated by the server
  props.org ? extractOrgProperties(cloneDeep(props.org)) : defaultOrg,
);

const chosenModules = ref<SysOrgModule[]>(localOrg.value.modules ?? []);

const allEmailDomains = ref<string[]>(getAllEmailDomains());
const chosenEmailDomains = ref<string[]>(
  localOrg.value.emailDomains
    ?.split(",")
    .filter((e) => e !== "")
    .map((e) => e.trim()) ?? [],
);

const chosenEntityDefinition = ref<string | undefined>(
  localOrg.value.entityDefinitionGuid ?? undefined,
);

// Entity definitions for the org being edited (not the current org)
const localEntityDefinitions = ref<EntityDefinitionListDto[]>([]);

// Whether this is a new org (no GUID) or an existing org
const isNewOrg = computed(() => !localOrg.value.guid);

// Load entity definitions for the specific org being edited
const loadEntityDefinitionsForOrg = async (sysOrgGuid: string) => {
  try {
    localEntityDefinitions.value =
      await apiClient.entityDefinition.getAllEntityDefinitionDtos(sysOrgGuid);
  } catch (error) {
    showError("Failed to load entity definitions for org:", error);
    localEntityDefinitions.value = [];
  }
};

// Watch for changes to the logo file GUID and load thumbnail
watch(
  () => localOrg.value?.logoFileGuid,
  (newFileGuid) => {
    if (newFileGuid) {
      loadLogo(newFileGuid);
    } else {
      logoImageUrl.value = null;
    }
  },
);

/** current open/close state of the dialog */
const dialogIsOpen = defineModel<boolean>({ required: true });

/** edit history for the org */
const editHistory = useRefHistory(localOrg, { deep: true });

/** 'true' if any changes have been made, and a save is needed */
const saveNeeded = computed(() => {
  const originalOrg = editHistory.history.value[0]?.snapshot;
  const currentOrg = editHistory.history.value.at(-1)?.snapshot;

  if (!originalOrg || !currentOrg) {
    return false;
  }

  // Extract only SysOrg properties for comparison to avoid OrgPlus property mismatch
  const orgChanged = !isEqual(
    extractOrgProperties(originalOrg),
    extractOrgProperties(currentOrg),
  );
  if (orgChanged) {
    // showError("SysOrg changed:", {
    //   originalOrg: extractOrgProperties(originalOrg),
    //   currentOrg: extractOrgProperties(currentOrg),
    // });
    return true;
  }

  const emailDomainsChanged = !arraysAreIdentical(
    localOrg.value.emailDomains
      ?.split(",")
      .filter((e) => e.trim() !== "")
      .map((e) => e.trim()) ?? [],
    chosenEmailDomains.value,
  );

  const modulesChanged = !arraysAreIdentical(
    localOrg.value.modules ?? [],
    chosenModules.value,
  );

  const entityDefinitionChanged =
    (localOrg.value.entityDefinitionGuid ?? "") !==
    (chosenEntityDefinition.value ?? "");

  console.log("Changes detected:", {
    emailDomainsChanged,
    entityDefinitionChanged,
    modulesChanged,
  });

  return emailDomainsChanged || entityDefinitionChanged || modulesChanged;
});

/** 'true' if the org has all required fields filled in */
const canSave = computed(() => localOrg.value.code && localOrg.value.name);

const handleUpdateOrg = async () => {
  if (!canSave.value) {
    showError("Please fill in all required fields.");
    return;
  }

  if (!localOrg.value) {
    return;
  }
  clearErrors();

  const org = toRaw(localOrg.value);

  org.emailDomains = chosenEmailDomains.value.join(", ");
  org.entityDefinitionGuid = chosenEntityDefinition.value;
  org.modules = chosenModules.value;
  org.loginRoleSysOrgs = []; // don't send these back to the server

  if (!org.guid) {
    // create if it doesnt exist already
    await apiClient.sysOrg
      .createSysOrg(org)
      .then((res: SysOrg) => {
        emit("add:org", res);
        ElMessage.success("Saved");
        // Update localOrg with server response (extract only SysOrg properties)
        localOrg.value = extractOrgProperties(res);
        // Update chosen values to match what was saved
        chosenEmailDomains.value =
          localOrg.value.emailDomains
            ?.split(",")
            .filter((e) => e.trim() !== "")
            .map((e) => e.trim()) ?? [];
        chosenEntityDefinition.value =
          localOrg.value.entityDefinitionGuid ?? undefined;
        chosenModules.value = localOrg.value.modules ?? [];
        // Clear history to start fresh from saved state
        editHistory.clear();
      })
      .catch(showError);
  } else {
    await apiClient.sysOrg
      // otherwise, we just update it
      .updateSysOrg(org.guid, org)
      .then((res: SysOrg) => {
        emit("update:org", res);
        ElMessage.success("Saved");
        // Update localOrg with server response (extract only SysOrg properties)
        localOrg.value = extractOrgProperties(res);
        // Update chosen values to match what was saved
        chosenEmailDomains.value =
          localOrg.value.emailDomains
            ?.split(",")
            .filter((e) => e.trim() !== "")
            .map((e) => e.trim()) ?? [];
        chosenEntityDefinition.value =
          localOrg.value.entityDefinitionGuid ?? undefined;
        chosenModules.value = localOrg.value.modules ?? [];
        // Clear history to start fresh from saved state
        editHistory.clear();
      })
      .catch(showError);
  }
};

const handleCloseDialog = () => {
  if (saveNeeded.value) {
    ElMessageBox.confirm("You have unsaved changes.", "Unsaved Changes", {
      cancelButtonText: "Discard",
      confirmButtonText: "Continue Editing",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    })
      // If the user confirms
      .then(() => {
        // do nothing
        // could use distinguishCancelAndClose: true to do something different
      })
      // If the user cancels
      .catch(() => {
        const originalOrg = editHistory.history.value.at(-1)?.snapshot;
        if (originalOrg) {
          localOrg.value = originalOrg;
        }
        dialogIsOpen.value = false;
      });
  } else {
    dialogIsOpen.value = false;
  }
};

const previewUrl = computed(() => {
  if (localOrg.value.websiteUrl && localOrg.value.websiteUrl.includes(".")) {
    return localOrg.value.websiteUrl.startsWith("http")
      ? localOrg.value.websiteUrl
      : `https://${localOrg.value.websiteUrl}`;
  }
  return "";
});

const handleLogoUpload = async (file: File) => {
  if (!localOrg.value?.guid) {
    showError("Please save the organization first before uploading a logo.");
    return false;
  }

  // Validate file type
  if (!file.type.startsWith("image/")) {
    showError("Please select an image file.");
    return false;
  }

  // Validate file size (10MB max)
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    showError("File size must be less than 10MB.");
    return false;
  }

  uploadingLogo.value = true;

  try {
    const response = await apiClient.fileUpload.uploadOrgLogo(
      localOrg.value.guid,
      { file },
    );

    if (response && response.guid) {
      localOrg.value.logoFileGuid = response.guid;
      // this was saved by the server, don't need to save here...
      // but there is no easy way to know if this is the only change
      ElMessage.success("Logo uploaded successfully!");
    }
    return true;
  } catch (error) {
    showError("Failed to upload logo. Please try again.", error);
  } finally {
    uploadingLogo.value = false;
  }

  return false; // Prevent default upload behavior
};

const removeLogo = () => {
  if (localOrg.value) {
    localOrg.value.logoFileGuid = null;
    showError("Logo removed successfully!");
  }
};

const handleExportOrg = async () => {
  if (!localOrg.value.guid) {
    showError("Cannot export: Organization not saved yet.");
    return;
  }

  exportingOrg.value = true;
  try {
    const jsonObject = await apiClient.sysOrg.exportSysOrgAsJson(
      localOrg.value.guid,
    );

    const blobUrl = URL.createObjectURL(
      new Blob([JSON.stringify(jsonObject, null, "\t")], {
        type: "application/json",
      }),
    );
    const downloadFileName = `${localOrg.value.name}.json`;

    const downloadNode = document.createElement("a");
    downloadNode.setAttribute("href", blobUrl);
    downloadNode.setAttribute("download", downloadFileName);
    downloadNode.click();
    downloadNode.remove();
    ElMessage.success({
      message: "Downloaded",
      grouping: true,
    });

    // The response should be a blob/file download
    // The browser should automatically handle the file download
    ElMessage.success("Organization data exported successfully!");
  } catch (error) {
    showError("Export failed", error);
  } finally {
    exportingOrg.value = false;
  }
};

const handleImportComplete = async () => {
  await orgStore.load();
  dialogIsOpen.value = false;
};

onMounted(async () => {
  await Promise.all([orgStore.load(), entityDefinitionStore.load()]);
  authToken.value = (await authStore.getAccessTokenAsync()) ?? undefined;
  await loadLogo(localOrg.value.logoFileGuid);

  // Load entity definitions for the specific org being edited (not for new orgs)
  if (!isNewOrg.value && localOrg.value.guid) {
    await loadEntityDefinitionsForOrg(localOrg.value.guid);
  }
});
</script>

<template>
  <el-drawer
    v-model="dialogIsOpen"
    class="EditOrganization"
    :size="drawerWidth"
    :close-on-click-modal="true"
    :before-close="handleCloseDialog"
  >
    <template #header>{{ localOrg?.name || "\u00A0" }}</template>
    <div>
      <el-tabs v-model="activeTabName" type="card">
        <el-tab-pane label="Edit" name="edit">
          <el-form
            v-if="localOrg"
            v-model="localOrg"
            label-width="200px"
            label-position="top"
            require-asterisk-position="right"
          >
            <div class="formOptions">
              <div>
                <el-form-item label="Code" required>
                  <el-input
                    v-model="localOrg.code"
                    :disabled="Boolean(localOrg.guid)"
                    show-word-limit
                    maxlength="5"
                  />
                </el-form-item>
                <el-form-item label="Name" required>
                  <el-input
                    v-model="localOrg.name"
                    show-word-limit
                    maxlength="250"
                  />
                </el-form-item>
                <el-form-item>
                  <template #label>
                    <span>
                      Website URL
                      <el-tooltip
                        effect="dark"
                        content="This is the link to your website."
                      >
                        <el-icon
                          style="color: #409eff; margin-left: 4px"
                          name="question"
                        />
                      </el-tooltip>
                    </span>
                    <el-link
                      v-if="previewUrl"
                      :href="previewUrl"
                      :target="previewUrl"
                      >View&nbsp;<el-icon>
                        <el-icon-link />
                      </el-icon>
                    </el-link>
                  </template>
                  <el-input
                    v-model="localOrg.websiteUrl"
                    show-word-limit
                    maxlength="250"
                  />
                </el-form-item>
              </div>
              <div>
                <el-form-item label="Contact Number">
                  <el-input
                    v-model="localOrg.contactNumber"
                    show-word-limit
                    maxlength="15"
                  /> </el-form-item
                ><el-form-item label="Email">
                  <el-input
                    v-model="localOrg.email"
                    show-word-limit
                    maxlength="35"
                  />
                </el-form-item>
                <el-form-item
                  v-if="!isNewOrg"
                  label="Default Entity Type for new User Accounts"
                >
                  <el-select
                    v-model="chosenEntityDefinition"
                    :empty-values="[null, undefined]"
                    clearable
                  >
                    <el-option
                      v-for="entity in localEntityDefinitions"
                      :key="entity.guid"
                      :label="entity.name ?? undefined"
                      :value="entity.guid"
                    />
                  </el-select>
                </el-form-item>
              </div>
              <div>
                <el-form-item label="Email Domains">
                  <el-select
                    v-model="chosenEmailDomains"
                    multiple
                    filterable
                    allow-create
                    default-first-option
                    :reserve-keyword="false"
                    placeholder="Select Email Domains"
                    style="width: 240px"
                  >
                    <el-option
                      v-for="emailDomain in allEmailDomains"
                      :key="emailDomain"
                      :label="emailDomain"
                      :value="emailDomain"
                    />
                  </el-select>
                </el-form-item>
                <el-form-item label="Enabled Modules">
                  <el-select
                    v-model="chosenModules"
                    multiple
                    placeholder="Select Modules"
                    style="width: 240px"
                  >
                    <el-option
                      v-for="module in Object.values(SysOrgModule)"
                      :key="module"
                      :label="module"
                      :value="module"
                    />
                  </el-select>
                </el-form-item>
                <el-form-item>
                  <template #label>
                    <span>
                      Organization Logo
                      <el-tooltip
                        effect="dark"
                        content="Upload an image to be used as the organization logo. Maximum file size is 10MB. Recommended dimensions: 200x60 pixels."
                      >
                        <el-icon
                          style="color: #409eff; margin-left: 4px"
                          name="question"
                        />
                      </el-tooltip>
                    </span>
                  </template>
                  <div class="logo-upload-section">
                    <div v-if="logoImageUrl" class="current-logo">
                      <img
                        :src="logoImageUrl"
                        alt="Organization Logo"
                        class="orgLogo"
                      />
                    </div>
                    <div class="uploadBtns">
                      <el-upload
                        ref="logoUploadRef"
                        :before-upload="handleLogoUpload"
                        :show-file-list="false"
                        accept="image/*"
                        :auto-upload="true"
                      >
                        <el-button type="primary">
                          {{ uploadingLogo ? "Uploading..." : "Upload Logo" }}
                        </el-button>
                      </el-upload>
                      <el-button
                        size="small"
                        type="danger"
                        plain
                        @click="removeLogo"
                      >
                        Remove Logo
                      </el-button>
                    </div>
                  </div>
                </el-form-item>
              </div>
            </div>
            <div class="buttons">
              <div>
                <el-button
                  type="primary"
                  :disabled="!(saveNeeded && canSave)"
                  @click="handleUpdateOrg"
                  ><u>S</u>ave</el-button
                >
                <el-button
                  plain
                  :type="saveNeeded ? 'danger' : 'default'"
                  @click="handleCloseDialog"
                  >{{ saveNeeded ? "Cancel" : "Close" }}</el-button
                >
              </div>
              <div>
                <el-button
                  v-if="localOrg.guid && myInfoStore.isSuperAdmin"
                  type="info"
                  plain
                  :loading="exportingOrg"
                  @click="handleExportOrg"
                  >Download as JSON</el-button
                >
                <el-button
                  v-if="localOrg.guid && myInfoStore.isSuperAdmin"
                  type="warning"
                  plain
                  @click="importDialogVisible = true"
                  >Import from JSON</el-button
                >
              </div>
            </div>
            <div class="formOptions full">
              <el-form-item>
                <template #label>
                  <span>
                    Custom CSS
                    <el-tooltip
                      effect="dark"
                      content="Custom CSS styles to override the default appearance for this organization."
                    >
                      <el-icon
                        style="color: #409eff; margin-left: 4px"
                        name="question"
                      />
                    </el-tooltip>
                  </span>
                </template>
                <el-input
                  v-model="localOrg.customCSS"
                  type="textarea"
                  :autosize="{ minRows: 4, maxRows: 30 }"
                  resize="both"
                  placeholder="Enter custom CSS styles here..."
                  show-word-limit
                  maxlength="5000"
                  class="custom-css-input"
                />
              </el-form-item>
            </div>
          </el-form>
        </el-tab-pane>
      </el-tabs>
    </div>

    <div class="form">
      <div class="sysOrgGuid">
        {{ localOrg.guid }}
      </div>
    </div>
  </el-drawer>
  <OrgJsonImporter
    v-model="importDialogVisible"
    :existing-sys-org-guid="localOrg.guid"
    @import:complete="handleImportComplete"
  />
</template>

<style lang="less">
.EditOrganization {
  &.ep-dialog {
    max-width: 660px;
    .ep-dialog__header {
      font-weight: bold;
      font-size: 22px;
    }
  }

  .el-dialog__header {
    font-size: 1.5em;
  }

  .orgLogo {
    max-height: 400px;
    max-width: 400px;
    object-fit: contain;
  }
  .uploadBtns {
    display: flex;
    gap: 10px;
  }

  .el-form-item__label {
    margin: 2em 0 0.25em -1em;
  }

  .formOptions {
    display: flex;
    flex-direction: row;
    gap: 5em;
    margin-top: -1em;
    overflow-y: auto;
    padding: 0 5em 0 2em;
    &.full {
      display: block;
      margin-top: 2em;
    }
  }

  .buttons {
    display: flex;
    justify-content: space-between;
    margin-top: 2em;
    .ep-form-item__content {
      justify-content: flex-end;
    }
  }

  .sysOrgGuid {
    font-size: 0.8rem;
    color: var(--el-color-primary-light-8);
    position: absolute;
    right: 5px;
    bottom: 5px;
  }

  .logo-upload-section {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .current-logo {
      display: flex;
      flex-direction: column;
      gap: 10px;
      align-items: flex-start;

      img {
        border: 1px solid #dcdfe6;
        padding: 1px;
      }
    }
  }
  .custom-css-input {
    textarea {
      font-size: 90%;
      overflow-y: auto;
      white-space: pre-wrap;
      font-family: monospace;
    }
  }
}
</style>
